import dateFormatter from '../../functions/dateFormatter.js';

const tryCatchWrapper =
  (executable, errors) =>
  async (...args) => {
    let result = '';
    try {
      result = await executable(...args);
    } catch (e) {
      if (e.status >= 500) {
        errors.push(e);
      }
    }
    return result;
  };

export default function (
  mergeContentsClient,
  contentProviderClient,
  sendClient
) {
  const mergeContentClient = mergeContentsClient;
  const contentClient = contentProviderClient;

  async function fetchMergeContents(
    accessToken,
    customer,
    {page = 0, pageSize = 10} = {}
  ) {
    const errors = [];
    const sources = {};
    const views = {};
    const uniqueViews = {};
    const tagSources = new Map();

    const response = await mergeContentClient.fetchMergeContents(
      accessToken,
      customer,
      {page, pageSize}
    );

    const getSourceName = tryCatchWrapper(retrieveSourceName, errors);
    const getViewName = tryCatchWrapper(retrieveViewName, errors);
    const getMergeTagsSourceName = tryCatchWrapper(
      retrieveMergeTagsSourceName,
      errors
    );

    const uniqueExternalSources = [
      ...new Set(
        response.content.map(c => {
          const sourceId = c['external-source-id'];
          const viewId = c['view-id'];

          if (!uniqueViews[sourceId]) {
            uniqueViews[sourceId] = new Set();
          }
          uniqueViews[sourceId].add(viewId);

          return sourceId;
        })
      )
    ];

    for (const s of uniqueExternalSources) {
      sources[s] = await getSourceName(accessToken, customer, s);
      for (const v of uniqueViews[s]) {
        views[v] = await getViewName(accessToken, customer, s, v);
      }
    }

    for (const content of response.content) {
      content['version-date'] = dateFormatter(content['version-date']);
      content['external-source-name'] = sources[content['external-source-id']];
      content['view-name'] = views[content['view-id']];

      if (
        content['external-source-name'] === '' ||
        content['view-name'] === ''
      ) {
        content.hasWarning = true;
      }

      if (content.rule.data.value.type === 'DYNAMIC') {
        const mergeTagsSourceId =
          content.rule.data.value.data['merge-tag-source-id'];
        let mergeTagsSourceName;

        if (tagSources.has(mergeTagsSourceId)) {
          mergeTagsSourceName = tagSources.get(mergeTagsSourceId);
        } else {
          mergeTagsSourceName = await getMergeTagsSourceName(
            accessToken,
            customer,
            mergeTagsSourceId
          );
          tagSources.set(mergeTagsSourceId, mergeTagsSourceName);
        }

        content.rule.data.value.data['merge-tag-source-name'] =
          mergeTagsSourceName;
        if (mergeTagsSourceName === '') content.hasWarning = true;
      }
    }

    return {...response, errors};
  }

  async function retrieveSourceName(accessToken, customer, source) {
    const sourceResponse = await contentClient.retrieveExternalSource(
      accessToken,
      customer,
      source
    );
    return sourceResponse.name;
  }

  async function retrieveViewName(accessToken, customer, source, view) {
    const viewResponse = await contentClient.retrieveView(
      accessToken,
      customer,
      source,
      view
    );
    return viewResponse.name;
  }

  async function retrieveMergeTagsSourceName(
    accessToken,
    customer,
    mergeTagsSource
  ) {
    const mergeTagsSourceResponse =
      await mergeContentClient.retrieveMergeSource(
        accessToken,
        customer,
        mergeTagsSource
      );
    return mergeTagsSourceResponse.name;
  }

  async function retrieveBasicFormData(accessToken, customer, dynamicContent) {
    const result = {};
    const errors = [];

    result.mergeContentId = dynamicContent.id;
    result.name = dynamicContent.name;

    try {
      result.externalSources = await contentClient.retrieveExternalSources(
        accessToken,
        customer
      );

      const source = result.externalSources.find(
        e => e.uuid === dynamicContent['external-source-id']
      );

      if (source !== undefined) {
        result.source = source.uuid;

        result.searchableFields = source.searchableFields;
        const field = result.searchableFields.find(
          f => f === dynamicContent.rule.data['external-source-field']
        );
        if (field !== undefined) {
          result.field = field;
        } else {
          errors.push('content.field_not_found');
        }

        result.views = await contentClient.retrieveViews(
          accessToken,
          customer,
          source.uuid
        );
        const view = result.views.find(v => v.id === dynamicContent['view-id']);
        if (view !== undefined) {
          result.view = view.id;
        } else {
          errors.push('content.view_not_found');
        }
      } else {
        errors.push('content.source_not_found');
      }
    } catch (error) {
      errors.push('content.load_source_info_error');
    }

    const valueType = dynamicContent.rule.data.value.type;

    result.dynamic = valueType !== 'STATIC';

    result.operator = dynamicContent.rule.data.operator;

    return {result, errors};
  }

  async function retrieveDynamicFormData(
    accessToken,
    customer,
    dynamicContent,
    {result, errors}
  ) {
    try {
      result.userDbs = await sendClient.retrieveUserDbs(accessToken, customer);
      const mergeSourceId =
        dynamicContent.rule.data.value.data['merge-tag-source-id'];
      const mergeSource = await mergeContentClient.retrieveMergeSource(
        accessToken,
        customer,
        mergeSourceId
      );

      result.userDb = mergeSource.extended.userDbId;
      result.userDbFields = await sendClient.retrieveFields(
        accessToken,
        customer,
        result.userDb
      );
    } catch (error) {
      errors.push('content.merge_tag_source_info_error');
    }
  }

  async function retrieveFormData(accessToken, customer, dynamicContent) {
    const {result, errors} = await retrieveBasicFormData(
      accessToken,
      customer,
      dynamicContent
    );

    if (result.dynamic) {
      await retrieveDynamicFormData(accessToken, customer, dynamicContent, {
        result,
        errors
      });
      result.userDbField = dynamicContent.rule.data.value.data['merge-tag'];
    } else {
      result.value = dynamicContent.rule.data.value.data['static-value'];
    }

    return {result, errors};
  }

  async function deleteMergeContent(accessToken, customer, dynamicContent) {
    await mergeContentClient.deleteMergeContent(
      accessToken,
      customer,
      dynamicContent
    );
  }

  return {
    fetchMergeContents,
    retrieveSourceName,
    retrieveViewName,
    retrieveMergeTagsSourceName,
    retrieveFormData,
    deleteMergeContent
  };
}
